import { useEffect, useState } from 'react';
import './Releases.css';

const EDGE_URL = 'https://edge.estream.dev';

interface Release {
  id: string;
  component: string;
  version: string;
  environment: string;
  gitCommit: string;
  gitRepo: string;
  changelog?: string;
  timestamp: number;
  publishedAt: string;
  deployedBy?: string;
  deployStatus?: 'available' | 'deploying' | 'deployed' | 'failed';
}

interface DeploymentHistory {
  id: string;
  releaseId: string;
  component: string;
  version: string;
  fromVersion?: string;
  environment: string;
  deployedBy: string;
  deployedAt: string;
  status: 'success' | 'failed' | 'rolled_back';
  circuitId?: string;
}

const COMPONENT_ICONS: Record<string, string> = {
  seeker: '📱',
  console: '💻',
  cli: '⌨️',
  'edge-proxy': '🌐',
  scatter: '🔀',
  kernel: '⚙️',
  eslite: '💾',
  default: '📦',
};

const ENVIRONMENTS = ['alpha-devnet', 'testnet', 'mainnet'] as const;
type Environment = typeof ENVIRONMENTS[number];

export default function Releases() {
  const [releases, setReleases] = useState<Release[]>([]);
  const [deploymentHistory, setDeploymentHistory] = useState<DeploymentHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'releases' | 'history'>('releases');
  const [deployingId, setDeployingId] = useState<string | null>(null);
  const [deployModal, setDeployModal] = useState<{ release: Release; env: Environment } | null>(null);
  const [rollbackModal, setRollbackModal] = useState<DeploymentHistory | null>(null);
  const [diffModal, setDiffModal] = useState<{ from: Release; to: Release } | null>(null);

  useEffect(() => {
    fetchReleases();
    fetchDeploymentHistory();
    const interval = setInterval(() => {
      fetchReleases();
      fetchDeploymentHistory();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  async function fetchReleases() {
    try {
      const response = await fetch(`${EDGE_URL}/api/releases?environment=alpha-devnet`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setReleases(data.releases || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch releases');
    } finally {
      setLoading(false);
    }
  }

  async function fetchDeploymentHistory() {
    try {
      const response = await fetch(`${EDGE_URL}/api/deployments?limit=50`);
      if (response.ok) {
        const data = await response.json();
        setDeploymentHistory(data.deployments || []);
      } else {
        // Use mock data if API doesn't exist
        setDeploymentHistory(getMockDeploymentHistory());
      }
    } catch {
      setDeploymentHistory(getMockDeploymentHistory());
    }
  }

  async function deployRelease(release: Release, environment: Environment) {
    setDeployingId(release.id);
    setDeployModal(null);
    
    try {
      const response = await fetch(`${EDGE_URL}/api/circuits/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'estream.ops.release.deploy.v1',
          environment,
          description: `Deploy ${release.component} v${release.version} to ${environment}`,
          definition: {
            component: release.component,
            version: release.version,
            releaseId: release.id,
            gitCommit: release.gitCommit,
          },
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to submit deployment circuit`);
      }
      
      setSuccess(`Deployment circuit submitted for ${release.component} v${release.version}. Approve via Seeker to deploy.`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Deployment failed');
    } finally {
      setDeployingId(null);
    }
  }

  async function rollbackDeployment(deployment: DeploymentHistory) {
    setRollbackModal(null);
    setDeployingId(deployment.id);
    
    try {
      const response = await fetch(`${EDGE_URL}/api/circuits/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'estream.ops.release.rollback.v1',
          environment: deployment.environment,
          description: `Rollback ${deployment.component} from v${deployment.version} to v${deployment.fromVersion}`,
          definition: {
            component: deployment.component,
            fromVersion: deployment.version,
            toVersion: deployment.fromVersion,
            originalDeploymentId: deployment.id,
          },
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to submit rollback circuit`);
      }
      
      setSuccess(`Rollback circuit submitted. Approve via Seeker to rollback.`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rollback failed');
    } finally {
      setDeployingId(null);
    }
  }

  const filteredReleases = filter === 'all' 
    ? releases 
    : releases.filter(r => r.component === filter);

  const components = [...new Set(releases.map(r => r.component))];

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getLatestByComponent = () => {
    const latest: Record<string, Release> = {};
    for (const r of releases) {
      if (!latest[r.component] || r.timestamp > latest[r.component].timestamp) {
        latest[r.component] = r;
      }
    }
    return Object.values(latest);
  };

  // Get all versions for a component, sorted by timestamp descending
  const getVersionsForComponent = (component: string): Release[] => {
    return releases
      .filter(r => r.component === component)
      .sort((a, b) => b.timestamp - a.timestamp);
  };

  // Get previous version for a release
  const getPreviousVersion = (release: Release): Release | undefined => {
    const versions = getVersionsForComponent(release.component);
    const idx = versions.findIndex(v => v.id === release.id);
    return idx >= 0 && idx < versions.length - 1 ? versions[idx + 1] : undefined;
  };

  if (loading) {
    return (
      <div className="releases-page">
        <div className="loading">Loading releases...</div>
      </div>
    );
  }

  return (
    <div className="releases-page">
      {/* Deploy Modal */}
      {deployModal && (
        <div className="modal-overlay" onClick={() => setDeployModal(null)}>
          <div className="deploy-modal" onClick={e => e.stopPropagation()}>
            <h3>Deploy Release</h3>
            <p>
              Deploy <strong>{deployModal.release.component}</strong> v{deployModal.release.version} to{' '}
              <strong>{deployModal.env}</strong>?
            </p>
            {deployModal.release.changelog && (
              <div className="modal-changelog">
                <h4>Changelog</h4>
                <p>{deployModal.release.changelog}</p>
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setDeployModal(null)}>Cancel</button>
              <button 
                className="btn btn-primary" 
                onClick={() => deployRelease(deployModal.release, deployModal.env)}
              >
                Deploy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rollback Modal */}
      {rollbackModal && (
        <div className="modal-overlay" onClick={() => setRollbackModal(null)}>
          <div className="deploy-modal" onClick={e => e.stopPropagation()}>
            <h3>Confirm Rollback</h3>
            <p className="warning-text">
              ⚠️ This will rollback <strong>{rollbackModal.component}</strong> from v{rollbackModal.version} 
              to v{rollbackModal.fromVersion || 'previous'} in <strong>{rollbackModal.environment}</strong>.
            </p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setRollbackModal(null)}>Cancel</button>
              <button 
                className="btn btn-danger" 
                onClick={() => rollbackDeployment(rollbackModal)}
              >
                Rollback
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version Diff Modal */}
      {diffModal && (
        <div className="modal-overlay" onClick={() => setDiffModal(null)}>
          <div className="diff-modal" onClick={e => e.stopPropagation()}>
            <h3>Version Comparison</h3>
            <div className="diff-header">
              <div className="diff-version diff-from">
                <span className="diff-label">From</span>
                <span className="version-badge">v{diffModal.from.version}</span>
                <span className="diff-date">{formatDate(diffModal.from.publishedAt)}</span>
              </div>
              <span className="diff-arrow">→</span>
              <div className="diff-version diff-to">
                <span className="diff-label">To</span>
                <span className="version-badge">v{diffModal.to.version}</span>
                <span className="diff-date">{formatDate(diffModal.to.publishedAt)}</span>
              </div>
            </div>
            
            <div className="diff-content">
              <div className="diff-section">
                <h4>Changes</h4>
                {diffModal.to.changelog ? (
                  <p className="diff-changelog">{diffModal.to.changelog}</p>
                ) : (
                  <p className="diff-no-changes">No changelog available</p>
                )}
              </div>
              
              <div className="diff-section">
                <h4>Commits</h4>
                <div className="diff-commits">
                  <a 
                    href={`https://github.com/${diffModal.to.gitRepo}/compare/${diffModal.from.gitCommit}...${diffModal.to.gitCommit}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary"
                  >
                    View Diff on GitHub
                  </a>
                  <div className="commit-range">
                    <code>{diffModal.from.gitCommit.slice(0, 7)}</code>
                    <span>→</span>
                    <code>{diffModal.to.gitCommit.slice(0, 7)}</code>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setDiffModal(null)}>Close</button>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setDiffModal(null);
                  setDeployModal({ release: diffModal.to, env: 'alpha-devnet' });
                }}
              >
                Deploy v{diffModal.to.version}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Banners */}
      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={fetchReleases}>Retry</button>
        </div>
      )}
      {success && (
        <div className="success-banner">
          <span>✓ {success}</span>
        </div>
      )}

      {/* Header with View Toggle */}
      <div className="releases-header">
        <h1>Releases</h1>
        <div className="view-toggle">
          <button 
            className={`toggle-btn ${viewMode === 'releases' ? 'active' : ''}`}
            onClick={() => setViewMode('releases')}
          >
            Available Releases
          </button>
          <button 
            className={`toggle-btn ${viewMode === 'history' ? 'active' : ''}`}
            onClick={() => setViewMode('history')}
          >
            Deployment History
          </button>
        </div>
      </div>

      {/* Latest Versions Summary */}
      <section className="latest-versions">
        <h2>Current Versions</h2>
        <div className="version-cards">
          {getLatestByComponent().map(release => (
            <div key={release.component} className="version-card">
              <span className="component-icon">
                {COMPONENT_ICONS[release.component] || COMPONENT_ICONS.default}
              </span>
              <div className="version-info">
                <span className="component-name">{release.component}</span>
                <span className="version-number">{release.version}</span>
              </div>
              <span className="version-date">
                {new Date(release.publishedAt).toLocaleDateString()}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Releases View */}
      {viewMode === 'releases' && (
        <>
          {/* Filter */}
          <div className="filter-bar">
            <label>Filter by component:</label>
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">All Components</option>
              {components.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <span className="release-count">{filteredReleases.length} release(s)</span>
          </div>

          {/* Release List */}
          <section className="release-history">
            <h2>Available Releases</h2>
            <div className="releases-list">
              {filteredReleases.map(release => (
                <div key={release.id} className="release-item">
                  <div className="release-header">
                    <span className="component-badge">
                      {COMPONENT_ICONS[release.component] || COMPONENT_ICONS.default}
                      {release.component}
                    </span>
                    <span className="version-badge">v{release.version}</span>
                    <span className="env-badge">{release.environment}</span>
                    <span className="release-date">{formatDate(release.publishedAt)}</span>
                  </div>
                  
                  {release.changelog && (
                    <p className="release-changelog">{release.changelog}</p>
                  )}
                  
                  <div className="release-meta">
                    <a 
                      href={`https://github.com/${release.gitRepo}/commit/${release.gitCommit}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="commit-link"
                    >
                      {release.gitCommit.slice(0, 7)}
                    </a>
                    <span className="repo-name">{release.gitRepo}</span>
                  </div>

                  {/* Deploy Actions */}
                  <div className="release-actions">
                    {(() => {
                      const prevVersion = getPreviousVersion(release);
                      return prevVersion ? (
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => setDiffModal({ from: prevVersion, to: release })}
                        >
                          Compare with v{prevVersion.version}
                        </button>
                      ) : null;
                    })()}
                    {ENVIRONMENTS.map(env => (
                      <button
                        key={env}
                        className={`btn btn-sm ${env === 'mainnet' ? 'btn-warning' : 'btn-secondary'}`}
                        onClick={() => setDeployModal({ release, env })}
                        disabled={deployingId === release.id}
                      >
                        {deployingId === release.id ? '...' : `Deploy to ${env}`}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              
              {filteredReleases.length === 0 && (
                <div className="empty-state">
                  No releases found for this filter.
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/* Deployment History View */}
      {viewMode === 'history' && (
        <section className="deployment-history">
          <h2>Deployment History</h2>
          <table className="table">
            <thead>
              <tr>
                <th>Component</th>
                <th>Version</th>
                <th>Environment</th>
                <th>Deployed By</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {deploymentHistory.map(deployment => (
                <tr key={deployment.id} className={`status-${deployment.status}`}>
                  <td>
                    <span className="component-badge">
                      {COMPONENT_ICONS[deployment.component] || COMPONENT_ICONS.default}
                      {deployment.component}
                    </span>
                  </td>
                  <td>
                    <span className="version-badge">v{deployment.version}</span>
                    {deployment.fromVersion && (
                      <span className="from-version">from v{deployment.fromVersion}</span>
                    )}
                  </td>
                  <td>
                    <span className={`env-badge env-${deployment.environment}`}>
                      {deployment.environment}
                    </span>
                  </td>
                  <td>
                    <span className="deployer">{deployment.deployedBy}</span>
                  </td>
                  <td>{formatDate(deployment.deployedAt)}</td>
                  <td>
                    <span className={`status-badge status-${deployment.status}`}>
                      {deployment.status}
                    </span>
                  </td>
                  <td>
                    {deployment.status === 'success' && deployment.fromVersion && (
                      <button 
                        className="btn btn-sm btn-danger"
                        onClick={() => setRollbackModal(deployment)}
                        disabled={deployingId === deployment.id}
                      >
                        Rollback
                      </button>
                    )}
                    {deployment.circuitId && (
                      <a 
                        href={`/circuits?id=${deployment.circuitId}`}
                        className="circuit-link"
                      >
                        View Circuit
                      </a>
                    )}
                  </td>
                </tr>
              ))}
              {deploymentHistory.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty-state">No deployment history</td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

// Mock deployment history for development
function getMockDeploymentHistory(): DeploymentHistory[] {
  return [
    {
      id: 'dep-001',
      releaseId: 'rel-001',
      component: 'console',
      version: '0.5.0',
      fromVersion: '0.4.9',
      environment: 'alpha-devnet',
      deployedBy: 'todd@estream.dev',
      deployedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      status: 'success',
      circuitId: 'circ-deploy-001',
    },
    {
      id: 'dep-002',
      releaseId: 'rel-002',
      component: 'edge-proxy',
      version: '0.3.2',
      fromVersion: '0.3.1',
      environment: 'alpha-devnet',
      deployedBy: 'todd@estream.dev',
      deployedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      status: 'success',
      circuitId: 'circ-deploy-002',
    },
    {
      id: 'dep-003',
      releaseId: 'rel-003',
      component: 'seeker',
      version: '1.2.0',
      fromVersion: '1.1.9',
      environment: 'alpha-devnet',
      deployedBy: 'todd@estream.dev',
      deployedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'success',
    },
  ];
}
